/**
 * @file SkuSelectRow.tsx
 * @description Строка одиночного выбора фасовки (phase19.24 этап 1, шаг 1,
 * решение пользователя 2026-09-20).
 *
 * Из панели документа «Подбор» открывает ОДИНОЧНЫЙ выбор: кладовщик добавляет
 * позиции по одной, ему не нужны галочки и кнопка «Добавить (3)». Галочки
 * остались только в массовом подборе из заголовка «Позиции».
 *
 * Уже добавленная фасовка не исчезает и не гаснет намертво: строка серая, с
 * подписью «уже добавлена», а клик по ней уводит к существующей строке
 * документа. Иначе кладовщик второй раз ищет то, что уже принял, и не
 * понимает, почему выбор «не сработал».
 *
 * Тач-цель ≥56px (warehouse-UI).
 */

import { CornerDownRight } from "lucide-react";

import { cn } from "@/lib/utils";

interface Props {
  /** «Иван-чай гранулированный — 50 г». */
  title: string;
  /** Артикул моноширинным. */
  code: string;
  /** Правая колонка: остаток, объём фасовки. */
  note?: string;
  /** Фасовка уже стоит в документе (в сборке — позиция закрыта). */
  added?: boolean;
  /**
   * Подпись причины рядом с артикулом. У документов это «уже добавлена», у
   * подбора в сборке — «собрано полностью» / «закрыто недостачей»: строка
   * недоступна по разным поводам, и повод обязан быть написан.
   */
  addedText?: string;
  onSelect: () => void;
  /** Переход к существующей строке документа; без него строка просто серая. */
  onGoToExisting?: () => void;
}

export function SkuSelectRow({
  title,
  code,
  note,
  added = false,
  addedText = "уже добавлена",
  onSelect,
  onGoToExisting,
}: Props) {
  return (
    <li>
      <button
        type="button"
        onClick={added ? onGoToExisting : onSelect}
        // Строку «уже добавлена» не дизейблим, когда есть переход: disabled
        // не ловит клик, и подсказка «нажмите, чтобы увидеть строку» была бы
        // обманом.
        disabled={added && !onGoToExisting}
        className={cn(
          "flex min-h-[56px] w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left",
          "hover:bg-surface-low disabled:opacity-50",
          added && "text-on-surface-variant",
        )}
      >
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-base font-medium">{title}</span>
          <span className="truncate font-mono text-sm text-on-surface-variant">
            {code}
            {added ? ` · ${addedText}` : ""}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5 text-sm tabular-nums">
          {note}
          {added && onGoToExisting ? (
            <CornerDownRight className="size-4" aria-label="Показать строку документа" />
          ) : null}
        </span>
      </button>
    </li>
  );
}
